import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { mockLiveStreams, mockMemorials } from '../data/mockData';
import StreamSearchHeader from '../components/Live/StreamSearchHeader';
import ErrorBoundary from '../components/Common/ErrorBoundary';
import StatusBanner from '../components/Live/StatusBanner';

const {
  FiVideo, FiUsers, FiExternalLink, FiMapPin, FiBriefcase,
  FiWifiOff, FiRefreshCw, FiCompass, FiBookOpen, FiArrowRight,
  FiSearch, FiFilter, FiCalendar,
} = FiIcons;

// ── Scheduled events from memorials ──────────────────────────────────────────
const allScheduledEvents = mockMemorials.flatMap(m =>
  (m.events || []).map(e => ({ ...e, organizer: m.name }))
);

// ── Upcoming sessions (mock) ──────────────────────────────────────────────────
const upcomingSessions = [
  {
    id: 'u1',
    title: 'Evening Vigil: Thompson Family',
    date: 'Tomorrow · 7:00 PM EST',
    attendees: 38,
    img: 'https://images.unsplash.com/photo-1465146344425-f00d5f5c8f07?q=80&w=800&auto=format&fit=crop',
  },
  {
    id: 'u2',
    title: 'Remembrance: Lin Arts Collective',
    date: 'Sat · 3:00 PM PST',
    attendees: 61,
    img: 'https://images.unsplash.com/photo-1460661419201-fd4cecdf8a8b?q=80&w=800&auto=format&fit=crop',
  },
  {
    id: 'u3',
    title: 'Morning Tribute: Chen Estate',
    date: 'Sun · 10:00 AM CST',
    attendees: 22,
    img: 'https://images.unsplash.com/photo-1556910103-1c02745aae4d?q=80&w=800&auto=format&fit=crop',
  },
];

// ── Floating particle ─────────────────────────────────────────────────────────
const Particle = ({ style }) => (
  <motion.div
    className="absolute rounded-full bg-white/10 pointer-events-none"
    animate={{ y: [0, -30, 0], opacity: [0.3, 0.7, 0.3] }}
    transition={{ duration: 4 + Math.random() * 3, repeat: Infinity, ease: 'easeInOut' }}
    style={style}
  />
);

// ── Upcoming session card ─────────────────────────────────────────────────────
const UpcomingCard = ({ session, idx, onClick }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.3 + idx * 0.1 }}
    whileHover={{ y: -4, scale: 1.02 }}
    onClick={onClick}
    className="group cursor-pointer bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl overflow-hidden flex items-center gap-4 p-3 hover:bg-white/20 transition-all duration-300"
  >
    <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0">
      <img src={session.img} alt={session.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-white font-bold text-sm leading-tight truncate">{session.title}</p>
      <p className="text-white/60 text-[10px] mt-0.5 flex items-center gap-1">
        <SafeIcon icon={FiCalendar} className="w-3 h-3" /> {session.date}
      </p>
    </div>
    <div className="flex-shrink-0 flex items-center gap-1 text-white/50 text-[10px]">
      <SafeIcon icon={FiUsers} className="w-3 h-3" /> {session.attendees}
    </div>
  </motion.div>
);

// ── Full-page immersive empty state ───────────────────────────────────────────
const EmptyState = ({ hasFilters, onReset, onExplore, onBrowseRecorded }) => {
  const particles = Array.from({ length: 12 }, (_, i) => ({
    width:  `${8 + Math.random() * 16}px`,
    height: `${8 + Math.random() * 16}px`,
    left:   `${Math.random() * 100}%`,
    top:    `${Math.random() * 100}%`,
    animationDelay: `${Math.random() * 4}s`,
  }));

  if (hasFilters) {
    // ── Filtered empty: clean, focused ──
    return (
      <motion.div
        key="filtered-empty"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -16 }}
        className="col-span-full"
      >
        <div className="relative overflow-hidden rounded-[40px] bg-gradient-to-br from-slate-800 via-slate-700 to-slate-900 shadow-2xl py-20 px-8 text-center">
          {/* Background image with overlay */}
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1519681393784-d120267933ba?q=80&w=2070&auto=format&fit=crop"
              alt=""
              className="w-full h-full object-cover opacity-10"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-slate-900/60 via-slate-800/80 to-slate-900/90" />
          </div>

          {/* Particles */}
          {particles.slice(0, 6).map((p, i) => <Particle key={i} style={p} />)}

          <div className="relative z-10">
            {/* Icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', delay: 0.1 }}
              className="w-20 h-20 rounded-full bg-white/10 backdrop-blur-md border border-white/20 flex items-center justify-center mx-auto mb-6 shadow-xl"
            >
              <SafeIcon icon={FiSearch} className="w-8 h-8 text-white/70" />
            </motion.div>

            <motion.h3
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-2xl sm:text-3xl font-serif text-white mb-3"
            >
              No live events match your search
            </motion.h3>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-white/60 text-sm max-w-md mx-auto mb-10 leading-relaxed"
            >
              We couldn't find any live sanctuaries matching your criteria. Try broadening your search or removing a filter.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <button
                onClick={onReset}
                className="flex items-center gap-2 px-7 py-3.5 rounded-full bg-white text-slate-800 text-xs font-black uppercase tracking-widest hover:bg-white/90 transition-all shadow-xl"
              >
                <SafeIcon icon={FiRefreshCw} className="w-3.5 h-3.5" />
                Clear All Filters
              </button>
              <button
                onClick={onExplore}
                className="flex items-center gap-2 px-7 py-3.5 rounded-full bg-white/10 border border-white/20 text-white text-xs font-black uppercase tracking-widest hover:bg-white/20 transition-all backdrop-blur-sm"
              >
                <SafeIcon icon={FiCompass} className="w-3.5 h-3.5" />
                Browse Memorials
              </button>
            </motion.div>
          </div>
        </div>
      </motion.div>
    );
  }

  // ── Global empty: full immersive hero ──
  return (
    <motion.div
      key="global-empty"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      className="col-span-full"
    >
      {/* ── Hero Panel ── */}
      <div className="relative overflow-hidden rounded-[40px] shadow-2xl mb-8" style={{ minHeight: 480 }}>
        {/* Thematic background */}
        <img
          src="https://images.unsplash.com/photo-1419242902214-272b3f66ee7a?q=80&w=2113&auto=format&fit=crop"
          alt="Peaceful night sky"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Deep gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/85 via-indigo-900/70 to-slate-900/90" />
        {/* Subtle radial glow */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(99,102,241,0.15)_0%,transparent_70%)]" />

        {/* Floating particles */}
        {particles.map((p, i) => <Particle key={i} style={p} />)}

        {/* Candlelight orbs */}
        <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-amber-900/20 to-transparent pointer-events-none" />
        {[...Array(5)].map((_, i) => (
          <motion.div
            key={`orb-${i}`}
            className="absolute bottom-8 rounded-full bg-amber-400/30 blur-xl pointer-events-none"
            style={{
              width:  `${40 + i * 15}px`,
              height: `${40 + i * 15}px`,
              left:   `${10 + i * 18}%`,
            }}
            animate={{ opacity: [0.2, 0.6, 0.2], scale: [1, 1.2, 1] }}
            transition={{ duration: 2.5 + i * 0.5, repeat: Infinity, ease: 'easeInOut' }}
          />
        ))}

        {/* Content */}
        <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-10 p-10 sm:p-14 h-full">

          {/* Left: main message */}
          <div className="flex-1 text-center lg:text-left">
            {/* Candle icon */}
            <motion.div
              initial={{ scale: 0, rotate: -10 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: 'spring', delay: 0.1 }}
              className="w-20 h-20 rounded-full bg-amber-400/20 border border-amber-400/30 flex items-center justify-center mx-auto lg:mx-0 mb-6 shadow-2xl shadow-amber-500/20"
            >
              <span className="text-4xl">🕯️</span>
            </motion.div>

            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="text-amber-300/80 text-[10px] font-black uppercase tracking-[0.3em] mb-3"
            >
              Eternal Sanctuary
            </motion.p>

            <motion.h2
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-3xl sm:text-4xl lg:text-5xl font-serif text-white leading-tight mb-4"
            >
              No Live Events<br />
              <span className="text-amber-300">Right Now</span>
            </motion.h2>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-white/60 text-sm sm:text-base leading-relaxed max-w-md mx-auto lg:mx-0 mb-8"
            >
              All sanctuaries are peacefully quiet at this moment. Browse our upcoming scheduled services or explore recorded sessions to find a tribute that speaks to you.
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4"
            >
              <button
                onClick={onExplore}
                className="group flex items-center gap-2.5 px-7 py-4 rounded-full bg-amber-400 text-slate-900 text-xs font-black uppercase tracking-widest hover:bg-amber-300 transition-all shadow-xl shadow-amber-500/30"
              >
                <SafeIcon icon={FiCompass} className="w-4 h-4" />
                Explore Memorials
                <SafeIcon icon={FiArrowRight} className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                onClick={onBrowseRecorded}
                className="flex items-center gap-2.5 px-7 py-4 rounded-full bg-white/10 border border-white/20 text-white text-xs font-black uppercase tracking-widest hover:bg-white/20 transition-all backdrop-blur-sm"
              >
                <SafeIcon icon={FiBookOpen} className="w-4 h-4" />
                Browse Recorded
              </button>
            </motion.div>
          </div>

          {/* Right: upcoming sessions panel */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.35 }}
            className="w-full lg:w-80 flex-shrink-0"
          >
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-5 shadow-2xl">
              <div className="flex items-center gap-2 mb-4">
                <SafeIcon icon={FiCalendar} className="w-4 h-4 text-amber-300" />
                <p className="text-white font-black text-xs uppercase tracking-widest">Upcoming Sessions</p>
              </div>
              <div className="space-y-3">
                {upcomingSessions.map((session, idx) => (
                  <UpcomingCard
                    key={session.id}
                    session={session}
                    idx={idx}
                    onClick={onExplore}
                  />
                ))}
              </div>
              <button
                onClick={onExplore}
                className="w-full mt-4 flex items-center justify-center gap-2 py-3 rounded-xl bg-white/10 hover:bg-white/20 border border-white/10 text-white/70 hover:text-white text-[10px] font-black uppercase tracking-widest transition-all"
              >
                View All Upcoming <SafeIcon icon={FiArrowRight} className="w-3 h-3" />
              </button>
            </div>
          </motion.div>
        </div>
      </div>

      {/* ── Info strip ── */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="grid grid-cols-1 sm:grid-cols-3 gap-4"
      >
        {[
          { icon: FiVideo,    color: 'from-indigo-500 to-blue-600',   label: 'Stream Services', desc: 'Watch live or recorded memorial services from anywhere in the world.' },
          { icon: FiFilter,   color: 'from-amber-500 to-orange-500',  label: 'Filter by Family', desc: 'Search by family name, location, or workplace to find the right tribute.' },
          { icon: FiCompass,  color: 'from-emerald-500 to-teal-600',  label: 'Explore Pages',  desc: 'Visit memorial pages to leave tributes, sign guestbooks, and share memories.' },
        ].map(({ icon, color, label, desc }, i) => (
          <motion.div
            key={label}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 + i * 0.08 }}
            className="bg-white rounded-3xl p-6 shadow-lg border border-brand-dark flex items-start gap-4"
          >
            <div className={`w-10 h-10 rounded-2xl bg-gradient-to-br ${color} flex items-center justify-center flex-shrink-0 shadow-lg`}>
              <SafeIcon icon={icon} className="w-4.5 h-4.5 text-white" />
            </div>
            <div>
              <p className="text-accent-navy font-black text-sm mb-1">{label}</p>
              <p className="text-text-muted text-xs leading-relaxed">{desc}</p>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </motion.div>
  );
};

// ── Stream Card ───────────────────────────────────────────────────────────────
const StreamCard = ({ stream, idx, onClick }) => (
  <motion.div
    layout
    key={stream.id}
    initial={{ opacity: 0, scale: 0.9 }}
    animate={{ opacity: 1, scale: 1 }}
    exit={{ opacity: 0, scale: 0.9 }}
    transition={{ delay: idx * 0.05 }}
    whileHover={{ y: -5 }}
    className="bg-white rounded-[32px] md:rounded-[40px] overflow-hidden shadow-xl border border-brand-dark group cursor-pointer"
    onClick={onClick}
  >
    <div className="relative aspect-video overflow-hidden">
      <img
        src={stream.thumbnail}
        alt={stream.title}
        className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
        onError={(e) => {
          e.target.src = 'https://images.unsplash.com/photo-1465146344425-f00d5f5c8f07?q=80&w=2076&auto=format&fit=crop';
        }}
      />
      <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-500">
        <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center text-white border border-white/30 transform scale-90 group-hover:scale-100 transition-transform">
          <SafeIcon icon={FiExternalLink} className="w-6 h-6 md:w-8 md:h-8" />
        </div>
      </div>
      <div className="absolute top-4 left-4 bg-red-600 text-white px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest flex items-center gap-2 shadow-lg">
        <span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" /> Live
      </div>
      <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-md text-white px-3 py-1 rounded-lg text-[10px] font-bold flex items-center gap-2 border border-white/10">
        <SafeIcon icon={FiUsers} className="w-3 h-3" /> {stream.viewers} Viewing
      </div>
    </div>
    <div className="p-8 md:p-10">
      <div className="flex flex-wrap gap-2 mb-4">
        {stream.location && (
          <span className="flex items-center gap-1.5 px-3 py-1 bg-brand-light text-accent-navy text-[9px] font-black uppercase tracking-tighter rounded-full border border-brand-dark">
            <SafeIcon icon={FiMapPin} className="w-2.5 h-2.5 text-accent-gold" /> {stream.location}
          </span>
        )}
        {stream.workPlace && (
          <span className="flex items-center gap-1.5 px-3 py-1 bg-brand-light text-accent-navy text-[9px] font-black uppercase tracking-tighter rounded-full border border-brand-dark">
            <SafeIcon icon={FiBriefcase} className="w-2.5 h-2.5 text-accent-gold" /> {stream.workPlace}
          </span>
        )}
      </div>
      <h3 className="text-xl md:text-2xl font-serif text-accent-navy mb-1 group-hover:text-accent-gold transition-colors">
        {stream.title}
      </h3>
      <p className="text-text-muted font-medium uppercase text-[9px] tracking-widest mb-6">
        Hosted by {stream.organizer}
      </p>
      <button className="w-full btn-primary flex items-center justify-center gap-3 py-4 text-sm shadow-xl shadow-accent-navy/10">
        <SafeIcon icon={FiVideo} /> Join Sanctuary
      </button>
    </div>
  </motion.div>
);

// ── Error Fallback ────────────────────────────────────────────────────────────
const LiveEventsFallback = (error) => (
  <div className="col-span-full py-24 text-center bg-red-50 rounded-[40px] border-2 border-dashed border-red-200">
    <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
      <span className="text-3xl">⚠️</span>
    </div>
    <h3 className="text-2xl font-serif text-red-700 mb-3">Failed to display live events</h3>
    <p className="text-red-500 text-sm max-w-sm mx-auto mb-6">
      {error?.message || 'An unexpected rendering error occurred. Please refresh the page.'}
    </p>
    <button
      onClick={() => window.location.reload()}
      className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-red-600 text-white text-xs font-black uppercase tracking-widest hover:bg-red-700 transition-colors"
    >
      <SafeIcon icon={FiRefreshCw} className="w-3.5 h-3.5" />
      Refresh Page
    </button>
  </div>
);

// ── Main Page ─────────────────────────────────────────────────────────────────
const LiveEvents = () => {
  const navigate = useNavigate();
  const [filteredStreams, setFilteredStreams] = useState(mockLiveStreams);
  const [searchTerm, setSearchTerm]           = useState('');
  const [filters, setFilters]                 = useState({ location: '', workPlace: '', familyName: '' });

  const hasActiveSearch =
    searchTerm.trim() !== '' || Object.values(filters).some(v => v !== '');

  const handleSearch = (term, activeFilters) => {
    setSearchTerm(term);
    const lower = term.toLowerCase();
    const results = mockLiveStreams.filter(stream => {
      const matchSearch =
        !term ||
        stream.title.toLowerCase().includes(lower) ||
        stream.deceasedName?.toLowerCase().includes(lower) ||
        stream.familyName?.toLowerCase().includes(lower) ||
        stream.organizer.toLowerCase().includes(lower);
      const matchLocation =
        !activeFilters.location ||
        stream.location?.toLowerCase().includes(activeFilters.location.toLowerCase());
      const matchWork =
        !activeFilters.workPlace ||
        stream.workPlace?.toLowerCase().includes(activeFilters.workPlace.toLowerCase());
      const matchFamily =
        !activeFilters.familyName ||
        stream.familyName?.toLowerCase().includes(activeFilters.familyName.toLowerCase());
      return matchSearch && matchLocation && matchWork && matchFamily;
    });
    setFilteredStreams(results);
  };

  const handleReset = () => {
    setFilters({ location: '', workPlace: '', familyName: '' });
    setSearchTerm('');
    setFilteredStreams(mockLiveStreams);
  };

  return (
    <div className="pt-32 min-h-screen bg-brand-light pb-20">
      <div className="container mx-auto px-4 sm:px-6">

        {/* ── Hero ── */}
        <div className="max-w-4xl mx-auto text-center mb-10">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl md:text-6xl font-serif text-accent-navy mb-6"
          >
            EternalStream
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-text-muted text-lg md:text-xl font-light leading-relaxed max-w-2xl mx-auto"
          >
            Find and join live remembrance services. Enter the name of the deceased, family, or location to locate a sanctuary.
          </motion.p>
        </div>

        {/* ── Dynamic Status Banner ── */}
        <StatusBanner
          streams={mockLiveStreams}
          scheduledEvents={allScheduledEvents}
        />

        {/* ── Search ── */}
        <StreamSearchHeader
          onSearch={handleSearch}
          filters={filters}
          setFilters={setFilters}
        />

        {/* ── Results count ── */}
        {hasActiveSearch && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center text-xs font-bold uppercase tracking-widest text-text-muted mb-6"
          >
            {filteredStreams.length === 0
              ? 'No results found'
              : `Showing ${filteredStreams.length} live event${filteredStreams.length !== 1 ? 's' : ''}`}
          </motion.p>
        )}

        {/* ── Grid with Error Boundary ── */}
        <ErrorBoundary fallback={LiveEventsFallback}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10 mb-16">
            <AnimatePresence mode="popLayout">
              {filteredStreams.length > 0 ? (
                filteredStreams.map((stream, idx) => (
                  <StreamCard
                    key={stream.id}
                    stream={stream}
                    idx={idx}
                    onClick={() => navigate(`/live/${stream.id}`)}
                  />
                ))
              ) : (
                <EmptyState
                  key="empty"
                  hasFilters={hasActiveSearch}
                  onReset={handleReset}
                  onExplore={() => navigate('/explore')}
                  onBrowseRecorded={() => navigate('/resources')}
                />
              )}
            </AnimatePresence>
          </div>
        </ErrorBoundary>

      </div>
    </div>
  );
};

export default LiveEvents;